import { LitElement, html, queryAsync } from 'lit-element'
import { Presence } from '@getgo/chameleon-core'
import { t } from '../../directives/translate'
import type { GoToDndSwitch } from '../dnd-switch/dnd-switch'
import type { KeyboardActivatable, WithForwardFocus } from '../../common'
import { isKeyboardEventFor } from '../../common'

/**
 * When a MenuItem has another "interactive" component such as a Switch,
 * we want to handle activation via keyboard and clicking a bit differently
 * than the default
 */

export class GoToAvatarPopoverMenuItemSwitch extends LitElement implements KeyboardActivatable, WithForwardFocus {
  static readonly tagName = 'goto-avatar-popover-menu-item-switch'

  @queryAsync('goto-dnd-switch')
  private readonly dndSwitch: Promise<GoToDndSwitch> | undefined

  /* istanbul ignore next */
  constructor() {
    // why ignore: to avoid istanbul saying it isn't tested.
    // Because it is! See avatar-popover-menu-item-switch.spec.ts
    // https://jira.ops.expertcity.com/browse/SCORE-1304
    super()
    /**
     * In this case, we want that component to know how to handle activation either
     * via click, or specific keyboard activation. Like a button would. But since
     * we’re in traversing ShadowDOMs, and we're following through activation to
     * an inner component, we have to explicitly
     * {@see GoToAvatarContainer.handlePopoverchange}
     *
     * Lastly, we're binding addEventListener at constructor as per lit-element docs.
     *
     * Bookmarks:
     * - https://lit-element.polymer-project.org/guide/events#add-event-listeners-in-the-constructor
     */
    this.addEventListener('click', this.activate)
    this.addEventListener('keydown', this.handleKeyboard)
    this.addEventListener('focus', this.directlyForwardFocusTo)
    // If we do not set the following, we would not be able to use up down
    // arrow with this switch as part of the group.
    this.setAttribute('role', 'menuitem')
    this.setAttribute('tabindex', '-1')
  }

  handleKeyboard = async (event: HTMLElementEventMap['keydown']) => {
    if (isKeyboardEventFor(event, ['Enter', 'NumpadEnter', 'Space'], 'keydown')) {
      await this.activate()
    }
  }

  /**
   * {@see WithForwardFocus.directlyForwardFocusTo}
   */
  /* istanbul ignore next */
  async directlyForwardFocusTo(): Promise<void> {
    // why ignore: to avoid istanbul saying it isn't tested.
    // Because it is! See avatar-popover-menu-item-switch.spec.ts
    // https://jira.ops.expertcity.com/browse/SCORE-1304
    const dndSwitch = await this.dndSwitch
    dndSwitch?.shadowRoot?.querySelector('chameleon-switch')?.focus()
  }

  /**
   * In this case, we’re interacting with a nested interactive subject,
   * that’s surrounded by chameleon-menu-item.
   *
   * {@see KeyboardActivatable.activate}
   */
  /* istanbul ignore next */
  async activate(): Promise<void> {
    // why ignore: to avoid istanbul saying it isn't tested.
    // Because it is! See avatar-popover-menu-item-switch.spec.ts
    // https://jira.ops.expertcity.com/browse/SCORE-1304
    const dndSwitch = await this.dndSwitch
    /**
     * In this case, it’s for a Switch component.
     * Should we want to do the same on an input text,
     * that would "on enter" change the user status, we could. But not now.
     * (besides, the handleKeydown ' ' would not allow a setting a status with it)
     */
    dndSwitch?.activate()
  }

  render() {
    return html`
      <chameleon-menu-item>
        <chameleon-presence-indicator
          slot="start"
          presence=${Presence.DND}
          aria-hidden="true"
        ></chameleon-presence-indicator>
        <chameleon-typography variant="body-small" id="dndSwitch"> ${t('Do not disturb')} </chameleon-typography>
        <goto-dnd-switch slot="end" aria-labelledby="dndSwitch"></goto-dnd-switch>
      </chameleon-menu-item>
    `
  }
}
declare global {
  interface HTMLElementTagNameMap {
    readonly [GoToAvatarPopoverMenuItemSwitch.tagName]: GoToAvatarPopoverMenuItemSwitch
  }
}
